import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// The editable HTML body of an outbound email draft (owner 2026-10-03: every outbound email opens an
// editor first). A browser re-serialises the HTML it is given (entities, quoting), so get() hands back
// the server's own string while the operator has changed nothing — an untouched draft goes out byte
// for byte — and the edited markup once they have.
export interface DraftBodyHandle { get: () => string; edited: () => boolean }

const baseStyle: React.CSSProperties = {
  minHeight: 160, maxHeight: 320, overflowY: 'auto', padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6,
  border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};

const DraftBody = forwardRef<DraftBodyHandle, { html: string; style?: React.CSSProperties; onEdit?: () => void }>(
  function DraftBody({ html, style, onEdit }, ref) {
    const el = useRef<HTMLDivElement | null>(null);
    const baseline = useRef('');
    useEffect(() => {
      if (!el.current) return;
      el.current.innerHTML = html;
      baseline.current = el.current.innerHTML;   // what the browser made of the untouched draft
    }, [html]);
    useImperativeHandle(ref, () => {
      const edited = () => !!el.current && el.current.innerHTML !== baseline.current;
      return { edited, get: () => (el.current && edited() ? el.current.innerHTML : html) };
    }, [html]);
    return <div ref={el} contentEditable suppressContentEditableWarning onInput={onEdit} style={{ ...baseStyle, ...style }} />;
  });

export default DraftBody;
